<template>
  <b-modal id="restriktionenExportModal" centered @show="onShow">
    <template #modal-header="{ close }">
      <div class="restriktionen-export__header">
        <span class="restriktionen-export__title">Sperrzeiten exportieren</span>
        <span class="restriktionen-export__close" @click="close()">
          <X :size="20" />
        </span>
      </div>
    </template>

    <div class="restriktionen-export__body">
      <FeldLabel :icon="labelIcons.Calendar" text="Zeitraum" />
      <RestriktionenSegmentedControl :options="zeitraumOptions" v-model="zeitraum" />
      <div class="restriktionen-export__row">
        <AppSelect
          v-if="zeitraum === 'month'"
          v-model="monat"
          label="Monat"
          hide-search
          placeholder="Monat wählen"
          :options="monatOptions"
        />
        <AppSelect v-model="jahr" label="Jahr" hide-search placeholder="Jahr wählen" :options="jahrOptions" />
      </div>
    </div>

    <template #modal-footer>
      <b-button
        class="dialog-btn-secondary"
        :disabled="isExporting"
        @click="$bvModal.hide('restriktionenExportModal')"
      >
        Abbrechen
      </b-button>
      <b-button class="dialog-btn" variant="primary" :disabled="isExporting" @click="onExport">
        <b-spinner v-if="isExporting" small class="mr-2" />
        Exportieren
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import moment from 'moment-timezone';
import { X, Calendar } from 'lucide-vue';
import RestriktionenSegmentedControl from './restriktionen-segmented-control.vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import FeldLabel from '@/components/common/feld-label.vue';
import { createSperrzeitenExcelExport } from '@/core/common/helpers/exportHelper.js';
import { DAY_KEY_FORMAT, monthNames } from './restriktionen-calendar.helper.js';

export default {
  name: 'RestriktionenExportModalComponent',
  components: {
    RestriktionenSegmentedControl,
    AppSelect,
    FeldLabel,
    X,
  },
  props: {
    currentDate: { type: [Object, String, Date], required: true },
    viewMode: { type: String, default: 'month' },
    laender: { type: Array, default: () => [] },
    reisen: { type: Array, default: () => [] },
  },
  data() {
    return {
      zeitraum: 'month',
      monat: moment().month(),
      jahr: moment().year(),
      isExporting: false,
      labelIcons: Object.freeze({ Calendar }),
      zeitraumOptions: [
        { value: 'month', text: 'Monat' },
        { value: 'year', text: 'Jahr' },
      ],
      monatOptions: monthNames.map((name, index) => ({ value: index, text: name })),
    };
  },
  computed: {
    jahrOptions() {
      const aktuellesJahr = moment().year();
      return Array.from({ length: 4 }, (_, index) => ({
        value: aktuellesJahr + index,
        text: String(aktuellesJahr + index),
      }));
    },
  },
  methods: {
    onShow() {
      // Vorauswahl aus dem angezeigten Kalenderzeitraum
      const basis = moment(this.currentDate);
      this.zeitraum = this.viewMode === 'year' ? 'year' : 'month';
      this.monat = basis.month();
      this.jahr = this.jahrOptions.some(option => option.value === basis.year())
        ? basis.year()
        : moment().year();
    },
    async onExport() {
      if (this.isExporting) {
        return;
      }
      const basis = moment({ year: this.jahr, month: this.zeitraum === 'month' ? this.monat : 0 });
      const von = basis.clone().startOf(this.zeitraum).format(DAY_KEY_FORMAT);
      const bis = basis.clone().endOf(this.zeitraum).format(DAY_KEY_FORMAT);
      const zeitraumText =
        this.zeitraum === 'month' ? `${monthNames[this.monat]}-${this.jahr}` : String(this.jahr);

      this.isExporting = true;
      try {
        await createSperrzeitenExcelExport({
          von,
          bis,
          laender: this.laender,
          reisen: this.reisen,
          fileNamePrefix: `Sperrzeiten_${zeitraumText}`,
        });
        this.$bvModal.hide('restriktionenExportModal');
      } catch (error) {
        // Fehler wurde bereits im Service angezeigt, der Dialog bleibt offen
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style scoped>
.restriktionen-export__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.restriktionen-export__title {
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: var(--text);
}

.restriktionen-export__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-muted);
}

.restriktionen-export__close:hover {
  color: var(--text-body);
}

.restriktionen-export__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.restriktionen-export__row {
  display: flex;
  gap: 12px;
}

.restriktionen-export__row > * {
  flex: 1;
  min-width: 0;
}
</style>
